import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Ticket, Users, Wallet, AlertTriangle, CheckCircle, ExternalLink, CalendarDays, PlayCircle, Info } from 'lucide-react';
import { GrasrotandelenBanner } from '../components/GrasrotandelenBanner';
import { SEO } from '../components/SEO';

import i18n from '../i18n';
import { useTranslation } from 'react-i18next';
const t = (k, o) => i18n.t(k, o);

const getlinks = () => [
  { label: t('pg.Fritidskortet.65b5a9'), title: t('pg.Fritidskortet.2dbcc2'), href: 'https://www.oslo.kommune.no/fritidskort' },
  { label: t('pg.Fritidskortet.65b5a9'), title: t('pg.Fritidskortet.e226b5'), href: 'https://www.oslo.kommune.no/natur-kultur-og-fritid/fritidskort-for-barn-og-unge/sok-om-fritidskort/' },
  { label: t('pg.Fritidskortet.65b5a9'), title: t('pg.Fritidskortet.3fa733'), href: 'https://www.oslo.kommune.no/natur-kultur-og-fritid/fritidskort-for-barn-og-unge/slik-bruker-du-fritidskortet/' },
  { label: t('pg.Fritidskortet.05fb4b'), title: t('pg.Fritidskortet.1f40bd'), href: 'https://oslo-fritidskort.friskus.com/fritidskortet' },
  { label: t('pg.Fritidskortet.8a981d'), title: t('pg.Fritidskortet.9dca39'), href: 'https://support.hoopit.io/nb/articles/4825773-betale-med-fritidskort-i-hoopit' },
];

const getsteps = () => [
  t('pg.Fritidskortet.94fd90'),
  t('pg.Fritidskortet.8aa77e'),
  t('pg.Fritidskortet.4616ae'),
  t('pg.Fritidskortet.0b568d'),
  t('pg.Fritidskortet.7c51bf'),
  t('pg.Fritidskortet.7fad49'),
  t('pg.Fritidskortet.ba7acd'),
];

const Card = ({ icon: Icon, title, children, testId }) => (
  <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 sm:p-8" data-testid={testId}>
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-green-500" aria-hidden="true" />
      </div>
      <div className="flex-1 min-w-0">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">{title}</h2>
        <div className="space-y-4 text-slate-600 dark:text-slate-300 leading-relaxed">{children}</div>
      </div>
    </div>
  </div>
);

export const Fritidskortet = () => {
  useTranslation();
  return (
  <div data-testid="fritidskortet-page" className="min-h-screen bg-slate-50 dark:bg-slate-900">
    <SEO
      title={t('pg.Fritidskortet.c4be9d')}
      description={t('pg.Fritidskortet.648c12')}
    />

    <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800">
      <div className="max-w-4xl mx-auto">
        <Link to="/pricing" data-testid="fritidskortet-back-link" className="inline-flex items-center gap-2 text-slate-500 hover:text-green-600 transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" />
          {t('pg.Fritidskortet.2f7f9d')}
        </Link>
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 bg-green-100 dark:bg-green-500/20 rounded-xl flex items-center justify-center">
            <Ticket className="w-7 h-7 text-green-600" />
          </div>
          <div>
            <p className="text-green-600 font-medium uppercase tracking-widest mb-1">{t('pg.Fritidskortet.7956a5')}</p>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900 dark:text-white">{t('pg.Fritidskortet.497741')}</h1>
          </div>
        </div>
        <p className="text-slate-600 dark:text-slate-300 text-lg leading-relaxed">
          {t('pg.Fritidskortet.336aab')}
        </p>
      </div>
    </section>

    <section className="px-4 sm:px-6 lg:px-8 py-12">
      <div className="max-w-4xl mx-auto space-y-6">
        <Card icon={Users} title={t('pg.Fritidskortet.3ab944')} testId="fritidskortet-who">
          <p>
            {t('pg.Fritidskortet.6cf91a')} <strong className="text-slate-900 dark:text-white">{t('pg.Fritidskortet.7bf6b6')}</strong> {t('pg.Fritidskortet.dc6821')}
          </p>
          <p>
            {t('pg.Fritidskortet.051395')} <strong className="text-slate-900 dark:text-white">{t('pg.Fritidskortet.693c7f')}</strong> {t('pg.Fritidskortet.1a165e')} <strong className="text-slate-900 dark:text-white">{t('pg.Fritidskortet.ad14af')}</strong> {t('pg.Fritidskortet.cac6f3')}
          </p>
        </Card>

        <Card icon={Wallet} title={t('pg.Fritidskortet.93d37a')} testId="fritidskortet-usage">
          <p>
            {t('pg.Fritidskortet.dee7dc')} <strong className="text-slate-900 dark:text-white">{t('pg.Fritidskortet.1cdd1a')}</strong> {t('pg.Fritidskortet.d442a6')}
          </p>
          <div className="bg-blue-500/10 border-l-4 border-blue-500 rounded-r-xl p-5">
            <p className="font-bold text-slate-900 dark:text-white mb-2">{t('pg.Fritidskortet.9a7055')}</p>
            <p>
              {t('pg.Fritidskortet.3b62a1')} <strong className="text-slate-900 dark:text-white">betale</strong> {t('pg.Fritidskortet.7c68d2')}
            </p>
          </div>
          <p>
            {t('pg.Fritidskortet.f57950')}
          </p>
        </Card>

        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 sm:p-8" data-testid="fritidskortet-video-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <PlayCircle className="w-5 h-5 text-green-500" aria-hidden="true" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">{t('pg.Fritidskortet.7cc258')}</h2>
          </div>
          <div className="mx-auto max-w-sm rounded-xl overflow-hidden bg-black shadow-md">
            <video
              data-testid="fritidskortet-video"
              controls
              playsInline
              preload="metadata"
              className="w-full aspect-[4/5]"
            >
              <source src="/videos/fritidskortet.mp4" type="video/mp4" />
              <source src="/videos/fritidskortet.webm" type="video/webm" />
              {t('pg.Fritidskortet.fa6b86')} <a href="/videos/fritidskortet.mp4">{t('pg.Fritidskortet.b52759')}</a>.
            </video>
          </div>
        </div>

        <Card icon={CheckCircle} title={t('pg.Fritidskortet.2cfd5a')} testId="fritidskortet-steps">
          <ol className="space-y-3 list-decimal list-inside marker:text-green-500 marker:font-bold">
            {getsteps().map((s) => <li key={s}>{s}</li>)}
          </ol>
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5">
            <p>
              <strong className="text-green-600">{t('pg.Fritidskortet.0ff45a')}</strong> {t('pg.Fritidskortet.dfd8b2')}
            </p>
          </div>
          <div className="bg-slate-100 dark:bg-slate-700/50 rounded-xl p-5">
            <p>
              <strong className="text-slate-900 dark:text-white">{t('pg.Fritidskortet.1a25e5')}</strong> {t('pg.Fritidskortet.3a99eb')}
            </p>
          </div>
        </Card>

        <Card icon={AlertTriangle} title={t('pg.Fritidskortet.24e9f1')} testId="fritidskortet-important">
          <p>
            {t('pg.Fritidskortet.e1ac24')} <strong className="text-slate-900 dark:text-white">{t('pg.Fritidskortet.a44488')}</strong>{t('pg.Fritidskortet.d7a607')}
          </p>
          <p>
            {t('pg.Fritidskortet.aebec4')}{' '}
            <Link to="/contact" className="text-green-600 hover:text-green-700 underline font-semibold">{t('pg.Fritidskortet.bf9f07')}</Link>.
          </p>
        </Card>

        <Card icon={CalendarDays} title={t('pg.Fritidskortet.04bc10')} testId="fritidskortet-year">
          <p>
            {t('pg.Fritidskortet.e80e72')}
          </p>
        </Card>

        <Card icon={ExternalLink} title={t('pg.Fritidskortet.d8354b')} testId="fritidskortet-getlinks()">
          <div className="grid gap-3">
            {getlinks().map((l) => (
              <a
                key={l.href}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-slate-100 dark:bg-slate-700/50 hover:bg-green-500/10 border border-slate-200 dark:border-slate-600 hover:border-green-500 rounded-xl p-4 transition-colors block"
              >
                <p className="text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider mb-1">{l.label}</p>
                <p className="text-slate-900 dark:text-white font-semibold">{l.title} →</p>
              </a>
            ))}
          </div>
        </Card>

        <Card icon={Info} title={t('pg.Fritidskortet.08a696')} testId="fritidskortet-contact">
          <p>
            {t('pg.Fritidskortet.237e73')}{' '}
            <Link to="/contact" className="text-green-600 hover:text-green-700 underline font-semibold">{t('pg.Fritidskortet.299699')}</Link>{t('pg.Fritidskortet.e26667')}
          </p>
        </Card>
      </div>
    </section>

    <GrasrotandelenBanner />
  </div>
  );
};

export default Fritidskortet;
